import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { JwtHelperService } from '@auth0/angular-jwt';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { environment } from '@src/environments/environment';
import { TOKEN_KEY, REFRESH_TOKEN_KEY, USER_KEY } from '@src/app/configs';
import { SessionEntity } from '@app/modules/auth/models';
import { LoginResponseDto } from '@app/modules/auth/models';
import { DeviceService } from '@app/shared/services/device.service';

/**
 * SessionService
 * Odpowiedzialny za:
 * - Zarządzanie tokenami (access, refresh)
 * - Storage (localStorage)
 * - Refresh token flow
 * - Session rotation
 */
@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private session$ = new BehaviorSubject<SessionEntity | null>(null);
  private jwtHelper = new JwtHelperService();
  private sessionRotationInProgress = false;
  private initialized = false;

  // Subject do powiadamiania o wylogowaniu (dla AuthService)
  private logoutRequiredSubject = new Subject<void>();
  public logoutRequired$ = this.logoutRequiredSubject.asObservable();

  constructor(
    private http: HttpClient,
    private deviceService: DeviceService
  ) {
    // ⚠️ NIE wywołuj initializeSession() tutaj - circular dependency!
    // Będzie wywołane lazy przy pierwszym dostępie do getSession/getAccessToken
  }

  /**
   * Pobierz observowalną sesję
   */
  get session(): Observable<SessionEntity | null> {
    this.ensureInitialized();
    return this.session$.asObservable();
  }

  /**
   * Pobierz aktualną sesję
   */
  getSession(): SessionEntity | null {
    this.ensureInitialized();
    return this.session$.value;
  }

  /**
   * Pobierz access token
   */
  getAccessToken(): string | null {
    this.ensureInitialized();
    return this.session$.value?.accessToken || null;
  }

  /**
   * Pobierz refresh token
   */
  getRefreshToken(): string | null {
    return this.session$.value?.refreshToken || null;
  }

  /**
   * Ustaw access token w nagłówku Authorization
   */
  getAuthorizationHeader(): string | null {
    const token = this.getAccessToken();
    return token ? `Bearer ${token}` : null;
  }

  /**
   * Czy token wygasa wkrótce (w ciągu 1 minuty)
   */
  isTokenExpiringSoon(): boolean {
    return this.session$.value?.isAccessTokenExpiringSoon || false;
  }

  /**
   * Czy token jest ważny
   */
  isTokenValid(): boolean {
    return this.session$.value?.isAccessTokenValid || false;
  }

  /**
   * Ustaw sesję
   */
  setSession(session: SessionEntity): void {
    this.session$.next(session);
    localStorage.setItem(TOKEN_KEY, session.accessToken);
    
    // DUAL MODE:
    // Web: refresh token jest w HttpOnly Cookie (NIE zapisujemy do localStorage)
    // Mobile: refresh token musi być zapisany do localStorage
    const isMobile = this.isMobilePlatform();
    
    if (session.refreshToken && isMobile) {
      localStorage.setItem(REFRESH_TOKEN_KEY, session.refreshToken);
      console.log('[SessionService] Refresh token saved to localStorage (mobile)');
    } else if (!isMobile) {
      // Web: usuń z localStorage (cookie jest używane)
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      console.log('[SessionService] Refresh token in HttpOnly Cookie (web)');
    }
  }

  /**
   * Wyczyść sesję (tokeny + dane użytkownika)
   * Wywoływane gdy refresh token wygasł lub jest nieważny
   */
  clearSession(): void {
    console.log('[SessionService] Clearing session (tokens + user data)');
    this.session$.next(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);  // Wyczyść też dane użytkownika
    
    // Powiadom AuthService o konieczności wylogowania
    this.logoutRequiredSubject.next();
  }

  /**
   * Refresh tokenu (session rotation)
   */
  refreshToken(): Observable<LoginResponseDto> {
    if (this.sessionRotationInProgress) {
      console.warn('[SessionService] Refresh already in progress, queuing...');
      return throwError(() => new Error('Session rotation in progress'));
    }

    this.sessionRotationInProgress = true;
    const session = this.session$.value;
    const isMobile = this.isMobilePlatform();
    
    // DUAL MODE:
    // Web: refresh token jest w HttpOnly Cookie (przeglądarka wyśle automatycznie z withCredentials)
    // Mobile: refresh token musi być w localStorage i wysłany w body
    const refreshToken = isMobile ? (session?.refreshToken || localStorage.getItem(REFRESH_TOKEN_KEY)) : null;

    console.log('[SessionService] Manual refresh token rotation...', {
      hasSession: !!session,
      platform: isMobile ? 'mobile' : 'web',
      hasRefreshToken: isMobile ? !!refreshToken : 'N/A (cookie)'
    });

    // Mobile: sprawdź czy mamy refresh token
    if (isMobile && !refreshToken) {
      console.error('[SessionService] Mobile: No refresh token available for rotation');
      this.sessionRotationInProgress = false;
      this.clearSession();
      return throwError(() => new Error('Refresh token not found'));
    }

    // DUAL MODE body:
    // Web: tylko deviceId (cookie się wyśle automatycznie)
    // Mobile: deviceId + refresh_token
    const body: any = { deviceId: this.deviceService.getDeviceId() };
    if (isMobile && refreshToken) {
      body.refresh_token = refreshToken;
    }
    
    console.log('[SessionService] Refresh request:', {
      platform: isMobile ? 'mobile' : 'web',
      hasRefreshInBody: !!body.refresh_token,
      withCredentials: !isMobile  // Web: true, Mobile: false
    });

    return this.http
      .post<LoginResponseDto>(
        `${environment.apiUrl}auth/refresh`,
        body,
        { withCredentials: true }  // WAŻNE: Przeglądarka wyśle HttpOnly Cookie!
      )
      .pipe(
        tap((response: LoginResponseDto) => {
          const expiresIn = this.getTokenExpiresInSeconds(response.access_token);
          const newSession = new SessionEntity(
            response.access_token,
            expiresIn,
            response.refresh_token
          );
          this.setSession(newSession);
          this.sessionRotationInProgress = false;
        }),
        catchError((error) => {
          this.sessionRotationInProgress = false;
          
          // Tylko wyloguj przy błędach autentykacji (401, 403)
          // NIE wyloguj przy błędach serwera (5xx) lub sieci (0, brak internetu)
          const status = error?.status;
          const isAuthError = status === 401 || status === 403;
          const isServerError = status >= 500 && status < 600;
          const isNetworkError = status === 0 || !status;
          
          if (isAuthError) {
            console.error('[SessionService] Auth error during refresh - clearing session', { status });
            this.clearSession();
          } else if (isServerError || isNetworkError) {
            console.warn('[SessionService] Server/network error during refresh - keeping session', { status });
            // NIE czyścimy sesji - użytkownik może spróbować ponownie
          } else {
            // Nieznany błąd - na wszelki wypadek wyloguj
            console.error('[SessionService] Unknown error during refresh - clearing session', { status });
            this.clearSession();
          }
          
          return throwError(() => error);
        })
      );
  }

  /**
   * Sprawdź czy to platforma mobile (dla dual-mode auth)
   * Web: HttpOnly Cookies
   * Mobile: Response Body + Secure Storage
   */
  private isMobilePlatform(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();
    return /mobile|android|ios|iphone|ipad|ipod/.test(userAgent);
  }

  /**
   * Zapewnij że sesja jest zainicjalizowana (lazy initialization)
   * WAŻNE: NIE używaj HTTP tutaj - circular dependency!
   */
  private ensureInitialized(): void {
    if (!this.initialized) {
      this.initialized = true;
      this.initializeSessionFromStorage();
    }
  }

  /**
   * Inicjalizuj sesję z localStorage - BEZ HTTP calls!
   * Refresh będzie obsłużony przez TokenRefreshInterceptor przy pierwszym API call.
   */
  private initializeSessionFromStorage(): void {
    const token = localStorage.getItem(TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);  // Tylko dla mobile
    const isMobile = this.isMobilePlatform();

    console.log('[SessionService] Initializing session from storage...', {
      platform: isMobile ? 'mobile' : 'web',
      hasAccessToken: !!token,
      hasRefreshToken: isMobile ? !!refreshToken : 'N/A (cookie)',
      timestamp: new Date().toISOString()
    });

    if (token) {
      try {
        const expiresInSeconds = this.getTokenExpiresInSeconds(token);
        const isAccessExpired = this.jwtHelper.isTokenExpired(token);
        
        console.log('[SessionService] Token status:', {
          accessExpired: isAccessExpired,
          expiresIn: expiresInSeconds
        });
        
        if (!isAccessExpired) {
          // Token jest ważny - ustaw sesję
          console.log('[SessionService] ✅ Access token valid, restoring session');
          // Web: nie przekazuj refreshToken (jest w cookie)
          // Mobile: przekaż refreshToken z localStorage
          this.session$.next(new SessionEntity(token, expiresInSeconds, isMobile ? refreshToken || undefined : undefined));
        } else {
          // Access token wygasł
          // DUAL MODE:
          // Web: HttpOnly Cookie może być ważne - zachowaj sesję, interceptor odświeży
          // Mobile: sprawdź czy jest refresh token w localStorage
          if (!isMobile) {
            // WEB: Zachowaj wygasły token - HttpOnly Cookie może być ważne
            console.log('[SessionService] ⚠️ Access token expired (web) - keeping session, will refresh on first API call');
            this.session$.next(new SessionEntity(token, 0, undefined));
          } else if (refreshToken) {
            // MOBILE: Mamy refresh token - zachowaj sesję
            console.log('[SessionService] ⚠️ Access token expired (mobile) - keeping session with refresh token');
            this.session$.next(new SessionEntity(token, 0, refreshToken));
          } else {
            // MOBILE: Brak refresh token - wyczyść
            console.log('[SessionService] ❌ Access token expired (mobile) and no refresh token - clearing session');
            this.clearSession();
          }
        }
      } catch (error) {
        console.error('[SessionService] Failed to initialize session:', error);
        this.clearSession();
      }
    } else {
      console.log('[SessionService] No access token found');
    }
  }

  /**
   * Wyciągnij czas wygaśnięcia z tokena JWT (w sekundach od teraz)
   * Publiczna - używana przez AuthService
   */
  getTokenExpiresInSeconds(token: string): number {
    try {
      const decoded = this.jwtHelper.decodeToken(token);
      if (decoded?.exp) {
        const expiresAt = decoded.exp * 1000; // exp jest w sekundach
        const now = Date.now();
        const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000));
        return remaining > 0 ? remaining : 900; // fallback 15 min
      }
    } catch (e) {
      console.warn('[SessionService] Failed to decode token exp:', e);
    }
    return 900; // fallback 15 min
  }
}
